import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { type ContentOwnership, compareContentOwners } from '@gitbook/app-database';
import { createPalette, type PaletteCommandDef, type PaletteDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    clipboard,
    confirm,
    DialogRoot,
    toast,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { apiClient, useLoggedInUser } from '../../state';
import { createPickContentOwnerPalette } from '../Content';
import { useCreateInOwner } from '../ContentListing';
import { InviteMembersDialog } from '../ShareContent/InviteMembersDialog';
import { useCreateSpaceCommands } from '../Space';
import { CreateCollectionDialog } from './CreateCollectionDialog';
import { RenameCollectionDialog } from './RenameCollectionDialog';

/**
 * Return common basic commands for a collection.
 */
export function useCollectionPalette(
    collection: api.Collection
): [() => PaletteDef, React.ReactElement] {
    const shareDialog = useDialogTriggerState();
    const renameDialog = useDialogTriggerState();
    const currentUser = useLoggedInUser();
    const owner: ContentOwnership = React.useMemo(
        () => ({
            organization: collection.organization,
            collection: collection.id,
        }),
        [collection]
    );
    const currentOwner: ContentOwnership = React.useMemo(
        () => ({
            organization: collection.organization,
            collection: collection.parent,
        }),
        [collection]
    );

    const [{ newSpace }, createSpaceElements] = useCreateSpaceCommands(owner);
    const [newCollectionCommand, createCollectionElements] = useCreateCollectionCommand(owner);
    const copyPalette = useCollectionCopyCommands(collection);

    const palette = () => {
        return createPalette({
            groups: [
                // Add content
                newSpace || newCollectionCommand
                    ? {
                          key: 'collection.add_content',
                          commands: [newSpace, newCollectionCommand].filter(filterOutNullable),
                      }
                    : null,
                // Move, Share
                {
                    key: 'collection',
                    commands: [
                        // Rename collection
                        collection.permissions.admin
                            ? {
                                  key: 'collection.rename',
                                  icon: Icon.Pencil,
                                  title: 'Rename',
                                  trigger: () => {
                                      renameDialog.open();
                                  },
                              }
                            : null,
                        // Share collection
                        collection.permissions.admin
                            ? {
                                  key: 'collection.share',
                                  icon: Icon.UserAdd,
                                  title: 'Permissions',
                                  trigger: () => {
                                      shareDialog.open();
                                  },
                              }
                            : null,
                        // Move collection
                        collection.permissions.admin
                            ? {
                                  key: 'collection.moveTo',
                                  icon: Icon.CornerUpRight,
                                  title: 'Move collection to…',
                                  descendant: () => {
                                      return createPickContentOwnerPalette({
                                          filter: (ancestors) => {
                                              return (
                                                  // We only support 2 level of collection nesting
                                                  ancestors.length <= 2 &&
                                                  // Don't allow moving the collection in itself
                                                  ancestors.some(
                                                      (ancestor) =>
                                                          ancestor.collection === collection.id
                                                  ) === false
                                              );
                                          },
                                          onPick: async (owner: ContentOwnership) => {
                                              if (
                                                  !compareContentOwners(currentOwner, owner, true)
                                              ) {
                                                  await toast.catch(async () => {
                                                      if (
                                                          currentOwner.organization ===
                                                          owner.organization
                                                      ) {
                                                          // Move collection in the same organization
                                                          await apiClient.collections.moveCollection(
                                                              collection.id,
                                                              {
                                                                  parent: owner.collection || null,
                                                              }
                                                          );
                                                      } else {
                                                          // Transfer collection to a different organization
                                                          await apiClient.collections.transferCollection(
                                                              collection.id,
                                                              {
                                                                  organization: owner.organization,
                                                              }
                                                          );
                                                      }
                                                  }, 'Error while moving collection');

                                                  analytics.logEvent(
                                                      analytics.createCollectionEvent(
                                                          collection,
                                                          analytics.createContentMoveEvent(
                                                              currentOwner,
                                                              owner,
                                                              'palette',
                                                              {
                                                                  type: 'collection_move',
                                                                  fromPrivate: false,
                                                                  toPrivate: false,
                                                              }
                                                          )
                                                      )
                                                  );
                                              }
                                          },
                                      });
                                  },
                              }
                            : null,
                        // Copy commands
                        copyPalette,
                    ].filter(filterOutNullable),
                },
                // Delete collection
                collection.permissions.admin
                    ? {
                          key: 'danger',
                          commands: [
                              {
                                  key: 'collection.delete',
                                  icon: Icon.Trashcan,
                                  title: 'Delete',
                                  trigger: () => {
                                      void confirm({
                                          kind: 'danger',
                                          title: 'Delete collection',
                                          description: (
                                              <div className="flex flex-col gap-6">
                                                  <p>
                                                      This collection and all of its data will be
                                                      deleted. The spaces contained in this
                                                      collection can be restored from trash for up
                                                      to 7 days but the collection itself{' '}
                                                      <strong>cannot</strong> be restored.
                                                  </p>
                                                  <Alert
                                                      size="xsmall"
                                                      details="Spaces that are linked to sites will not be
                                                      deleted. Instead, they will be moved to their
                                                      parent collections or to the root."
                                                  />
                                              </div>
                                          ),

                                          confirmLabel: 'Delete',
                                          onConfirm: async (_event, { preventClose }) => {
                                              await toast.catch(
                                                  async () => {
                                                      if (!currentUser) {
                                                          return;
                                                      }

                                                      await apiClient.collections.deleteCollectionById(
                                                          collection.id
                                                      );

                                                      toast.success('Collection deleted');
                                                  },
                                                  'Error while deleting the collection',
                                                  {
                                                      onReject: preventClose,
                                                  }
                                              );
                                          },
                                      });
                                  },
                              },
                          ],
                      }
                    : null,
            ].filter(filterOutNullable),
        });
    };

    return [
        palette,
        <>
            <DialogRoot {...shareDialog.rootProps} key={collection.id}>
                <InviteMembersDialog content={{ collection }} />
            </DialogRoot>
            <DialogRoot {...renameDialog.rootProps}>
                <RenameCollectionDialog collection={collection} />
            </DialogRoot>
            {createSpaceElements}
            {createCollectionElements}
        </>,
    ];
}

/**
 * Command to create collections from a palette menu.
 */
export const useCreateCollectionCommand = (
    owner: ContentOwnership
): [PaletteCommandDef, React.ReactElement] | [null, null] => {
    const { createCollection } = useCreateInOwner(owner);
    const dialogTrigger = useDialogTriggerState();
    const openDialog = useEventCallback(() => {
        dialogTrigger.open();
    });
    const canCreateCollection = Boolean(createCollection);
    const command = React.useMemo(() => {
        return canCreateCollection
            ? {
                  key: 'new_collection',
                  icon: Icon.CollectionNew,
                  title: 'New collection',
                  subtitle: 'Group your spaces',
                  trigger: () => {
                      openDialog();
                  },
              }
            : null;
    }, [canCreateCollection, openDialog]);

    return command
        ? [
              command,
              <DialogRoot key="create-collection-dialog" {...dialogTrigger.rootProps}>
                  <CreateCollectionDialog
                      collectionId={owner.collection}
                      organizationId={owner.organization}
                  />
              </DialogRoot>,
          ]
        : [null, null];
};

/**
 * Return collection Copy commands.
 * Mainly useful for development purposes.
 */
export function useCollectionCopyCommands(collection: api.Collection): PaletteCommandDef {
    return React.useMemo(() => {
        return {
            key: 'collection.copy',
            icon: Icon.Copy,
            title: 'Copy',
            descendant: () => {
                return createPalette({
                    groups: [
                        {
                            key: 'collection.copy',
                            commands: [
                                {
                                    key: 'collection.copyId',
                                    icon: Icon.ID,
                                    title: 'Copy ID',
                                    trigger: () => {
                                        clipboard.writeText(collection.id);
                                    },
                                },
                                {
                                    key: 'collection.copyTitle',
                                    icon: Icon.Type,
                                    title: 'Copy title',
                                    trigger: () => {
                                        clipboard.writeText(collection.title);
                                    },
                                },
                            ],
                        },
                    ],
                });
            },
        };
    }, [collection]);
}
